import { ChevronDown, X } from "lucide-react";
import type { ReactNode } from "react";
import { useListbox } from "../../hooks/useListbox";
import { usePopoverPosition } from "../../hooks/usePopoverPosition";
import { Badge, type BadgeVariant } from "../Badge/Badge";
import { IconButton } from "../IconButton/IconButton";
import { MenuOption } from "../Menu/MenuOption";

export type TagVariant = "citation" | "category" | "property" | "attachment";
export type TagColor = "gray" | "orange" | "purple" | "green" | "blue" | "yellow";

export interface TagPropertyOption {
  value: string;

  color?: BadgeVariant;
}

const BORDER_CLASSES: Record<TagColor, string> = {
  gray: "border-border-subtle",
  orange: "border-brand-primary",
  purple: "border-brand-secondary",
  green: "border-success",
  blue: "border-info",
  yellow: "border-warning",
};

const DOT_CLASSES: Record<TagColor, string> = {
  gray: "bg-content-tertiary",
  orange: "bg-brand-primary",
  purple: "bg-brand-secondary",
  green: "bg-success",
  blue: "bg-info",
  yellow: "bg-warning",
};

export interface TagProps {
  variant?: TagVariant;
  children?: ReactNode;

  icon?: ReactNode;

  onClick?: () => void;

  disabled?: boolean;

  color?: TagColor;

  dot?: boolean;

  href?: string;

  truncate?: boolean;

  onRemove?: () => void;

  previewUrl?: string;

  detail?: string;

  label?: string;

  options?: TagPropertyOption[];

  value?: string;

  onChange?: (value: string) => void;

  placeholder?: string;
}

export function Tag({
  variant = "citation",
  children,
  icon,
  onClick,
  disabled = false,
  color = "gray",
  dot = true,
  href,
  truncate = false,
  onRemove,
  previewUrl,
  detail,
  label,
  options = [],
  value,
  onChange,
  placeholder = "Add",
}: Readonly<TagProps>) {
  const {
    isOpen,
    setIsOpen,
    highlightedIndex,
    setHighlightedIndex,
    selectedIndex,
    containerRef,
    triggerRef,
    optionRefs,
    listboxId,
    optionId,
    openAt,
    commitSelection,
    handleTriggerKeyDown,
  } = useListbox({
    id: label ?? "tag-property",
    options,
    value,
    onChange: onChange ?? (() => {}),
    disabled,
  });

  const { panelRef, style: panelStyle } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorRef: triggerRef,
    align: "center",
  });

  if (variant === "property") {
    const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined;

    return (
      <div ref={containerRef} className="relative isolate inline-block">
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-activedescendant={isOpen ? optionId(highlightedIndex) : undefined}
          aria-label={`${label}: ${selectedOption?.value ?? placeholder}`}
          onClick={() => (isOpen ? setIsOpen(false) : openAt(selectedIndex >= 0 ? selectedIndex : 0))}
          onKeyDown={handleTriggerKeyDown}
          className={[
            "group btn-focus-ring inline-flex items-center gap-1 rounded-lg py-1 pl-1.5 pr-1 transition-colors hover:bg-surface-base",
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          ].join(" ")}
        >
          <span className="text-xs text-content-tertiary transition-colors group-hover:text-content-secondary">
            {label}
          </span>
          <Badge
            size="compact"
            variant={selectedOption?.color ?? "neutral"}
            trailingIcon={<ChevronDown size={12} className="shrink-0" />}
          >
            {selectedOption?.value ?? placeholder}
          </Badge>
        </button>
        {isOpen && (
          <div
            ref={panelRef}
            role="listbox"
            id={listboxId}
            aria-label={label}
            style={panelStyle}
            className="z-[110] max-h-64 w-44 overflow-auto rounded-xl border border-border-input bg-surface-elevated px-1 py-1 shadow-lg"
          >
            {options.map((option, index) => (
              <MenuOption
                key={option.value}
                role="option"
                size="sm"
                id={optionId(index)}
                optionRef={(node) => {
                  optionRefs.current[index] = node;
                }}
                highlighted={index === highlightedIndex}
                selected={index === selectedIndex}
                onHighlight={() => setHighlightedIndex(index)}
                onSelect={() => commitSelection(index)}
              >
                {option.value}
              </MenuOption>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (variant === "attachment") {
    return (
      <span className="inline-flex h-6 max-w-64 items-center gap-1.5 rounded-lg border border-border-input bg-surface-secondary pr-1 pl-1.5">
        {previewUrl ? <img src={previewUrl} alt="" className="size-4 shrink-0 rounded-xs object-cover" /> : icon}
        <span className="min-w-0 truncate text-xs font-medium text-content-primary">{children}</span>
        {detail && <span className="shrink-0 text-xs text-content-tertiary">{detail}</span>}
        {onRemove && (
          <IconButton
            variant="ghost"
            size="xs"
            onClick={onRemove}
            aria-label={`Remove ${typeof children === "string" ? children : "attachment"}`}
            icon={<X />}
          />
        )}
      </span>
    );
  }

  if (variant === "category") {
    const content = truncate ? <span className="min-w-0 max-w-44 truncate">{children}</span> : children;
    const tagLabel = href ? (
      <a href={href} className="btn-focus-ring min-w-0 rounded hover:underline">
        {content}
      </a>
    ) : (
      content
    );

    return (
      <span
        className={[
          "inline-flex h-6 items-center gap-1.5 rounded-md border bg-surface-base pl-2 text-xs font-medium text-content-secondary",
          onRemove ? "pr-1" : "pr-2",
          BORDER_CLASSES[color],
          disabled ? "opacity-50" : "",
        ].join(" ")}
      >
        {dot && <span className={["size-1.5 shrink-0 rounded-full", DOT_CLASSES[color]].join(" ")} />}
        {tagLabel}
        {onRemove && (
          <IconButton
            variant="ghost"
            size="xs"
            disabled={disabled}
            onClick={onRemove}
            aria-label={`Remove ${typeof children === "string" ? children : "tag"}`}
            icon={<X />}
          />
        )}
      </span>
    );
  }

  const citationClass =
    "btn-focus-ring inline-flex h-6 max-w-full items-center gap-1.5 rounded-md border border-border-subtle bg-surface-base px-2 font-mono text-xs text-content-secondary transition-colors hover:border-border-strong";
  const citationContent = (
    <>
      {icon && <span className="shrink-0">{icon}</span>}
      {truncate ? <span className="min-w-0 truncate">{children}</span> : children}
    </>
  );
  const fullText = truncate && typeof children === "string" ? children : undefined;

  // A citation with a URL is a link to the source, opened in a new tab.
  if (href && !disabled) {
    return (
      <a href={href} target="_blank" rel="noreferrer" title={fullText} className={`${citationClass} cursor-pointer`}>
        {citationContent}
      </a>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={fullText}
      className={[citationClass, disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"].join(" ")}
    >
      {citationContent}
    </button>
  );
}
